import React from 'react';
import { LucideTrees as Tree, LucideDroplet as Droplet, LucideUtensils as Utensils, LucideBookOpen as BookOpen, LucideHeart as Heart, LucideGlobe as Globe, LucideUsers as Users, LucideTrendingUp as TrendingUp } from 'lucide-react';

interface ImpactStats {
  treesPlanted: number;
  lessonsCompleted: number;
  mealsProvided: number;
  waterGallons: number;
  livesImpacted: number;
  carbonOffset: number;
}

const ImpactDashboard: React.FC<{ userStats: { level: number } }> = ({ userStats }) => {
  // Calculate impact based on lessons completed
  const calculateImpact = (): ImpactStats => {
    const lessonsCompleted = Math.floor(userStats.level * 12); // Estimate based on level
    return {
      treesPlanted: Math.floor(lessonsCompleted / 12), // 1 tree per 12 lessons
      lessonsCompleted,
      mealsProvided: Math.floor(lessonsCompleted / 8), // 1 meal per 8 lessons
      waterGallons: lessonsCompleted * 10, // 10 gallons per lesson
      livesImpacted: Math.floor(lessonsCompleted / 20), // Impact 1 life per 20 lessons
      carbonOffset: Math.floor(lessonsCompleted / 5), // 1 kg CO2 offset per 5 lessons
    };
  };

  const impact = calculateImpact();

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl shadow-lg p-4 w-full max-h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">Your Impact Dashboard</h2>
        <div className="px-3 py-1.5 bg-white rounded-full shadow-md text-xs font-medium text-emerald-600 flex items-center">
          <Globe className="w-3.5 h-3.5 mr-1.5" />
          Global Impact
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {/* Trees Planted */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Tree className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-2xl font-bold text-gray-800 leading-none mb-0.5">{impact.treesPlanted}</h3>
              <p className="text-xs text-gray-600">Trees Planted</p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500 transition-all duration-500"
              style={{ width: `${(impact.treesPlanted / 100) * 100}%` }}
            />
          </div>
        </div>

        {/* Water Donated */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Droplet className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-2xl font-bold text-gray-800 leading-none mb-0.5">{impact.waterGallons}</h3>
              <p className="text-xs text-gray-600">Gallons of Water</p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all duration-500"
              style={{ width: `${(impact.waterGallons / 1000) * 100}%` }}
            />
          </div>
        </div>

        {/* Meals Provided */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Utensils className="w-5 h-5 text-orange-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-2xl font-bold text-gray-800 leading-none mb-0.5">{impact.mealsProvided}</h3>
              <p className="text-xs text-gray-600">Meals Provided</p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-orange-500 transition-all duration-500"
              style={{ width: `${(impact.mealsProvided / 100) * 100}%` }}
            />
          </div>
        </div>

        {/* Lessons Completed */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-2xl font-bold text-gray-800 leading-none mb-0.5">{impact.lessonsCompleted}</h3>
              <p className="text-xs text-gray-600">Lessons Completed</p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-purple-500 transition-all duration-500"
              style={{ width: `${(impact.lessonsCompleted / 200) * 100}%` }}
            />
          </div>
        </div>

        {/* Lives Impacted */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Heart className="w-5 h-5 text-red-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-2xl font-bold text-gray-800 leading-none mb-0.5">{impact.livesImpacted}</h3>
              <p className="text-xs text-gray-600">Lives Impacted</p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-red-500 transition-all duration-500"
              style={{ width: `${(impact.livesImpacted / 50) * 100}%` }}
            />
          </div>
        </div>

        {/* Carbon Offset */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-teal-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-2xl font-bold text-gray-800 leading-none mb-0.5">{impact.carbonOffset}</h3>
              <p className="text-xs text-gray-600">kg CO₂ Offset</p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-teal-500 transition-all duration-500"
              style={{ width: `${(impact.carbonOffset / 100) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <button className="flex items-center justify-center px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 transition-colors">
          <Tree className="w-4 h-4 mr-2" />
          Plant More Trees
        </button>
        <button className="flex items-center justify-center px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors">
          <Users className="w-4 h-4 mr-2" />
          Share Impact
        </button>
      </div>
    </div>
  );
};

export default ImpactDashboard;